import React, { useEffect, useRef, useState } from 'react';

/**
 * Process-menu dropdown — the Genesys "process navigation" selector shared by
 * the customer screens (faithful to the customer's Figma accessible dropdown).
 *
 * Pure navigation chooser: it surfaces the caller-supplied list of process
 * options and reports the selected option via {@link onSelect}. It does NOT
 * perform navigation itself, and it does NOT grant access — the caller wires
 * each option to a route, and the route remains independently RBAC-gated
 * (deny-by-default) by the router, so a menu entry can never bypass a screen's
 * permission gate.
 */
export function ProcessMenu({
  options,
  onSelect,
  id = 'process-menu',
  placeholder = 'Process Menu',
}: {
  /** The process options to list, in display order. */
  options: readonly string[];
  /** Called with the chosen option when the agent selects one. */
  onSelect: (option: string) => void;
  /** DOM id for the trigger button (and its `aria` wiring). */
  id?: string;
  /** Placeholder text shown before a selection is made. */
  placeholder?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  function choose(option: string): void {
    setValue(option);
    setOpen(false);
    onSelect(option);
  }

  return (
    <div className="relative w-[260px]" ref={ref}>
      <button
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
      >
        <span className={value ? '' : 'text-[#6b7280]'}>
          {value || placeholder}
        </span>
        <svg
          className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Process menu"
          className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
        >
          {options.map((option) => (
            <li
              key={option}
              role="option"
              aria-selected={value === option}
              tabIndex={0}
              onClick={() => choose(option)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  choose(option);
                }
              }}
              className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                value === option ? 'bg-[#e5e7eb] font-bold' : ''
              }`}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
